import {
  Popover,
  PopoverContent,
  HStack,
  IconButton,
  ListItem,
  UnorderedList,
  Tooltip,
  Box,
  Text,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import { GrDownload } from "react-icons/gr";
import { FaMoneyBillWave, FaTrash } from "react-icons/fa";
import { MdAutorenew } from "react-icons/md";
import GarrazCopyText from "./GaraazCopyText";
import React, { useCallback } from "react";
import { downloadInvoiceForOrder } from "../orderUtils";
import { useGetAllSuppliers } from "../queryHooks";
import { capitalize } from "lodash";
import { GaraazCopyText } from "@svan-garaaz/reactsharedlib";
import VoidOrderInvoiceConfirmationDialog from "./VoidOrderInvoiceConfirmationDialog";
import VoidedInvoicesNestedPopover, {
  VoidedInvoice,
} from "./VoidedInvoicesNestedPopover";
import { OrderStatus } from "../enums";
import RegenerateInvoiceDialog, {
  type VirtualInvoiceRegenerateTradingParams,
} from "./RegenerateInvoiceDialog";
import {
  formatInvoiceCollectorDisplay,
  formatInvoicePercentageDisplay,
  getDeleteInvoiceDisabled,
  getDeleteInvoiceTooltip,
  paymentHasCollector,
  isInvoiceTypeKnown,
} from "../utils/invoicePopoverUtils";

type InvoicePopoverProps = {
  children?: React.ReactNode;
  Invoices: any[];
  copierKeys: any[];
  Challans: any[];
  CreditNotes?: any[];
  voidedInvoices?: VoidedInvoice[];
  canVoidInvoice?: boolean;
  canRegenerateOrderInvoice?: boolean;
  canRegenerateDeliveryInvoice?: boolean;
  hasOrderInvoice?: boolean;
  hasDeliveryInvoice?: boolean;
  onRegenerateOrderInvoice?: (
    tradingParams?: VirtualInvoiceRegenerateTradingParams,
  ) => Promise<void>;
  onRegenerateDeliveryInvoice?: (deliveryCost: number) => Promise<void>;
  currentDeliveryCost?: number;
  minimumDeliveryCost?: number;
  orderStatus?: OrderStatus;
  orderNo?: string;
  onVoidSuccess?: () => void;
  isVirtualInvoiceRegenerationMode?: boolean;
  isSingleInvoiceRequired?: boolean;
  discountPercentage?: number | string;
  creditNotePercentage?: number | string;
};

const InvoicePopver: React.FC<InvoicePopoverProps> = ({
  Invoices,
  children,
  copierKeys,
  Challans,
  CreditNotes,
  voidedInvoices,
  canVoidInvoice,
  canRegenerateOrderInvoice,
  canRegenerateDeliveryInvoice,
  hasOrderInvoice,
  hasDeliveryInvoice,
  onRegenerateOrderInvoice,
  onRegenerateDeliveryInvoice,
  currentDeliveryCost,
  minimumDeliveryCost,
  orderNo,
  onVoidSuccess,
  isVirtualInvoiceRegenerationMode = false,
  isSingleInvoiceRequired = false,
  discountPercentage,
  creditNotePercentage,
}) => {
  const { data: allSuppliers } = useGetAllSuppliers();
  const canRegenerateOrderInvoiceOption =
    !!canRegenerateOrderInvoice && !!onRegenerateOrderInvoice;
  const canRegenerateDeliveryInvoiceOption =
    !!canRegenerateDeliveryInvoice && !!onRegenerateDeliveryInvoice;
  const {
    isOpen: isVoidDialogOpen,
    onOpen: onVoidDialogOpen,
    onClose: onVoidDialogClose,
  } = useDisclosure();
  const {
    isOpen: isRegenerateDialogOpen,
    onOpen: onRegenerateDialogOpen,
    onClose: onRegenerateDialogClose,
  } = useDisclosure();
  const [voidInvoiceNo, setVoidInvoiceNo] = React.useState("");
  const [voidInvoiceType, setVoidInvoiceType] = React.useState<
    "ORDER" | "DELIVERY"
  >("ORDER");
  const getTitleCaseFromCamelCase = (word: string) => {
    const result = word.replace(/([A-Z])/g, " $1");
    const formatted = result.charAt(0).toUpperCase() + result.slice(1);
    return formatted
      .replace("Return Credit Note", "CN")
      .replace("Credit Note", "CN")
      .replace("Consolidated Invoice Eligible Order Nos", "Orders Included");
  };

  const getSupplierBusinessName = useCallback(
    (supplierId: string) => {
      const supplier = allSuppliers?.find(
        (supplier: any) => supplier._id === supplierId,
      );
      return supplier ? supplier.businessName : "";
    },
    [allSuppliers],
  );

  const excludedKeys = [
    "title",
    "invoiceType",
    "invoiceMediaId",
    "mrpInvoiceMediaId",
    "supplierId",
    "challanMediaId",
    "creditNoteMediaId",
    "paymentLink",
    "voidedBy",
    "payment",
  ];

  return (
    <Popover
      returnFocusOnClose={false}
      trigger="hover"
      // Open upward from the footer so the panel is not clipped by the next card
      placement="top-end"
      strategy="fixed"
      gutter={8}
      closeDelay={400}
    >
      {children}

      {/* Portal so the panel is not clipped by the card overflow */}
      <Portal>
      <PopoverContent
        zIndex={2000}
        width="400px"
        maxWidth="90vw"
        maxHeight="70vh"
        overflowY="auto"
        p="10px"
        height="auto"
      >
        {(Invoices?.some((item) => item?.invoiceNo) ||
          canRegenerateOrderInvoiceOption ||
          canRegenerateDeliveryInvoiceOption ||
          isVirtualInvoiceRegenerationMode) && (
          <>
            <HStack justifyContent={"space-between"} mb="2">
              <Text color={"#4A5567"} fontSize={"lg"} fontWeight={"bold"}>
                Invoice Info
              </Text>
              <HStack spacing={2} alignItems="center">
                {(canRegenerateOrderInvoiceOption ||
                  canRegenerateDeliveryInvoiceOption) && (
                  <Tooltip
                    label={
                      isVirtualInvoiceRegenerationMode
                        ? !isSingleInvoiceRequired
                          ? "Single invoice generation is only allowed for single invoice required enquiries"
                          : "Generate Consolidated Order Invoice"
                        : "Regenerate Garaaz Invoice(s)"
                    }
                    placement="top"
                  >
                    <span>
                      <IconButton
                        icon={<MdAutorenew />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRegenerateDialogOpen();
                        }}
                        aria-label="Regenerate Garaaz Invoice(s)"
                        size="xs"
                        h="28px"
                        minW="28px"
                        w="28px"
                        fontSize="20px"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="green.200"
                        bg="green.100"
                        color="green.700"
                        transition="background 0.15s ease, border-color 0.15s ease"
                        _hover={{
                          bg: "green.200",
                          borderColor: "green.300",
                        }}
                        _active={{ bg: "green.300" }}
                        isDisabled={
                          isVirtualInvoiceRegenerationMode &&
                          !isSingleInvoiceRequired
                        }
                      />
                    </span>
                  </Tooltip>
                )}
                {(voidedInvoices?.length ?? 0) > 0 && (
                  <VoidedInvoicesNestedPopover
                    voidedInvoices={voidedInvoices!}
                  />
                )}
              </HStack>
            </HStack>

            <Box textColor={"#4A5567"}>
              <UnorderedList fontSize={"sm"}>
                {Invoices?.map((item: any, index: number) =>
                  item?.invoiceNo ? (
                    <ListItem key={index}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Text fontWeight="bold">
                          {item?.title ??
                            `${getSupplierBusinessName(item?.supplierId)}`}
                        </Text>
                        <HStack spacing={1} flexShrink={0}>
                          {item?.paymentLink && (
                            <Tooltip label="Copy payment link" placement="top">
                              <Box
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                cursor="pointer"
                                color="teal.600"
                                _hover={{ color: "teal.700" }}
                              >
                                <GaraazCopyText
                                  text={item.paymentLink}
                                  popOverMessage="Payment link copied"
                                  key={item.paymentLink}
                                  icon={<FaMoneyBillWave />}
                                />
                              </Box>
                            </Tooltip>
                          )}

                          <Tooltip label="Download Invoice" placement="top">
                            <IconButton
                              icon={<GrDownload />}
                              onClick={() => {
                                downloadInvoiceForOrder(item?.invoiceMediaId);
                              }}
                              aria-label="download Invoice"
                              size="xs"
                              bg="none"
                            />
                          </Tooltip>
                          {item?.mrpInvoiceMediaId &&
                            (item?.title === "Garaaz" || !item?.supplierId) && (
                              <Tooltip
                                label="Download MRP Invoice"
                                placement="top"
                              >
                                <IconButton
                                  icon={<GrDownload />}
                                  onClick={() => {
                                    downloadInvoiceForOrder(
                                      item?.mrpInvoiceMediaId,
                                    );
                                  }}
                                  aria-label="download MRP Invoice"
                                  size="xs"
                                  bg="green.100"
                                />
                              </Tooltip>
                            )}
                          {canVoidInvoice &&
                            (item?.title === "Garaaz" || !item?.supplierId) && (
                              <Tooltip
                                label={getDeleteInvoiceTooltip(
                                  item?.invoiceStatus,
                                  item?.invoiceType,
                                )}
                                placement="top"
                              >
                                <span>
                                  <IconButton
                                    icon={<FaTrash />}
                                    onClick={() => {
                                      const t = item?.invoiceType;
                                      if (!isInvoiceTypeKnown(t)) {
                                        return;
                                      }
                                      setVoidInvoiceNo(item?.invoiceNo);
                                      setVoidInvoiceType(t);
                                      onVoidDialogOpen();
                                    }}
                                    aria-label="delete invoice"
                                    size="xs"
                                    bg="red.50"
                                    color="red.500"
                                    _hover={{ bg: "red.100" }}
                                    _disabled={{
                                      opacity: 0.5,
                                      cursor: "not-allowed",
                                    }}
                                    isDisabled={getDeleteInvoiceDisabled(
                                      item?.invoiceStatus,
                                      item?.invoiceType,
                                    )}
                                  />
                                </span>
                              </Tooltip>
                            )}
                        </HStack>
                      </Box>

                      <UnorderedList
                        key={index}
                        listStyleType={"none"}
                        marginLeft={0}
                        marginBottom={"1em"}
                      >
                        {Object?.entries(item)?.map(
                          ([key, value]: [any, any], fieldIdx) => {
                            const hasValue = value != null && value !== "";
                            const displayValue = Array.isArray(value)
                              ? value.join(", ")
                              : (value ?? "-");
                            const showPercentages =
                              key === "invoiceAmount" &&
                              !item?.supplierId &&
                              item?.invoiceType !== "DELIVERY";
                            const discountDisplay = showPercentages
                              ? formatInvoicePercentageDisplay(
                                  item.discountPercentage ??
                                    item.discount ??
                                    discountPercentage,
                                )
                              : null;
                            const creditNoteDisplay = showPercentages
                              ? formatInvoicePercentageDisplay(
                                  item.creditNotePercentage ??
                                    creditNotePercentage,
                                )
                              : null;

                            if (!excludedKeys.includes(key) && hasValue) {
                              return (
                                <React.Fragment key={fieldIdx}>
                                  <ListItem
                                    display={"flex"}
                                    alignItems={"center"}
                                  >
                                    <>
                                      {" "}
                                      <Text fontSize={"sm"} minWidth={"100px"}>
                                        {getTitleCaseFromCamelCase(key)}
                                      </Text>
                                      <Text fontSize={"sm"} marginRight={"5px"}>
                                        :
                                      </Text>{" "}
                                    </>

                                    <Tooltip label={String(displayValue)}>
                                      <Text fontSize={"sm"} noOfLines={1}>
                                        {String(displayValue)}
                                      </Text>
                                    </Tooltip>
                                    {copierKeys.includes(key) && (
                                      <GarrazCopyText
                                        text={String(displayValue)}
                                        popOverMessage={`${getTitleCaseFromCamelCase(key)} Copied`}
                                      ></GarrazCopyText>
                                    )}
                                  </ListItem>
                                  {discountDisplay && (
                                    <ListItem
                                      display={"flex"}
                                      key={`${fieldIdx}-discount`}
                                      alignItems={"center"}
                                    >
                                      <Text fontSize={"sm"} minWidth={"100px"}>
                                        Discount
                                      </Text>
                                      <Text fontSize={"sm"} marginRight={"5px"}>
                                        :
                                      </Text>
                                      <Tooltip label={discountDisplay}>
                                        <Text fontSize={"sm"} noOfLines={1}>
                                          {discountDisplay}
                                        </Text>
                                      </Tooltip>
                                    </ListItem>
                                  )}
                                  {creditNoteDisplay && (
                                    <ListItem
                                      display={"flex"}
                                      key={`${fieldIdx}-credit-note`}
                                      alignItems={"center"}
                                    >
                                      <Text fontSize={"sm"} minWidth={"100px"}>
                                        Credit Note
                                      </Text>
                                      <Text fontSize={"sm"} marginRight={"5px"}>
                                        :
                                      </Text>
                                      <Tooltip label={creditNoteDisplay}>
                                        <Text fontSize={"sm"} noOfLines={1}>
                                          {creditNoteDisplay}
                                        </Text>
                                      </Tooltip>
                                    </ListItem>
                                  )}
                                </React.Fragment>
                              );
                            }

                            return <React.Fragment key={fieldIdx} />;
                          },
                        )}
                        {paymentHasCollector(item?.payment) && (
                          <ListItem
                            display={"flex"}
                            key={`${index}-collected-by`}
                            alignItems={"center"}
                          >
                            <Text fontSize={"sm"} minWidth={"90px"}>
                              Received By
                            </Text>
                            <Text fontSize={"sm"} marginRight={"5px"}>
                              :
                            </Text>
                            <Tooltip
                              label={formatInvoiceCollectorDisplay(
                                item?.payment,
                              )}
                            >
                              <Text fontSize={"sm"} noOfLines={1}>
                                {formatInvoiceCollectorDisplay(item?.payment)}
                              </Text>
                            </Tooltip>
                          </ListItem>
                        )}
                      </UnorderedList>
                    </ListItem>
                  ) : (
                    <React.Fragment key={index} />
                  ),
                )}
              </UnorderedList>
            </Box>
          </>
        )}
        {Challans?.some((item) => item?.challanNo) && (
          <>
            <HStack justifyContent={"space-between"} mb="2">
              <Text color={"#4A5567"} fontSize={"lg"} fontWeight={"bold"}>
                Challan Info
              </Text>
            </HStack>
            <Box textColor={"#4A5567"}>
              <UnorderedList fontSize={"sm"}>
                {Challans?.map((item: any, index: number) =>
                  item?.challanNo ? (
                    <ListItem key={index}>
                      <Box display={"flex"} justifyContent={"space-between"}>
                        {" "}
                        <Text fontWeight={"bold"}>
                          {item?.title ??
                            `${getSupplierBusinessName(item?.supplierId)}`}
                        </Text>
                        <IconButton
                          icon={<GrDownload />}
                          onClick={() => {
                            downloadInvoiceForOrder(item?.challanMediaId);
                          }}
                          aria-label="download"
                          size={"xs"}
                          bg={"none"}
                        />{" "}
                      </Box>

                      <UnorderedList
                        key={index}
                        listStyleType={"none"}
                        marginLeft={0}
                        marginBottom={"1em"}
                      >
                        {Object?.entries(item)?.map(
                          ([key, value]: [any, any], index) => {
                            const displayValue = Array.isArray(value)
                              ? value.join(", ")
                              : value;
                            const hasValue =
                              displayValue != null &&
                              displayValue !== "" &&
                              !(Array.isArray(value) && value.length === 0);
                            return !excludedKeys.includes(key) && hasValue ? (
                              <ListItem
                                display={"flex"}
                                key={index}
                                alignItems={"center"}
                              >
                                <>
                                  {" "}
                                  <Text fontSize={"sm"} minWidth={"90px"}>
                                    {getTitleCaseFromCamelCase(key)}
                                  </Text>
                                  <Text fontSize={"sm"} marginRight={"5px"}>
                                    :
                                  </Text>{" "}
                                </>

                                <Tooltip label={String(displayValue)}>
                                  <Text fontSize={"sm"} noOfLines={2}>
                                    {String(displayValue)}
                                  </Text>
                                </Tooltip>
                                {copierKeys.includes(key) && (
                                  <GarrazCopyText
                                    text={String(displayValue)}
                                    popOverMessage={`${key} Copied`}
                                  ></GarrazCopyText>
                                )}
                              </ListItem>
                            ) : (
                              <React.Fragment key={index} />
                            );
                          },
                        )}
                      </UnorderedList>
                    </ListItem>
                  ) : (
                    <React.Fragment key={index} />
                  ),
                )}
              </UnorderedList>
            </Box>
          </>
        )}
        {CreditNotes?.some((item) => item?.creditNoteNo) && (
          <>
            <HStack justifyContent={"space-between"} mb="2">
              <Text color={"#4A5567"} fontSize={"lg"} fontWeight={"bold"}>
                Credit Note Info
              </Text>
            </HStack>
            <Box textColor={"#4A5567"}>
              <UnorderedList fontSize={"sm"}>
                {CreditNotes?.map((item: any, index: number) =>
                  item?.creditNoteNo ? (
                    <ListItem key={index}>
                      <Box display={"flex"} justifyContent={"space-between"}>
                        {" "}
                        <Text fontWeight={"bold"}>
                          {capitalize(item?.type ?? "Garaaz")}
                        </Text>
                        {item?.creditNoteMediaId && (
                          <IconButton
                            icon={<GrDownload />}
                            onClick={() => {
                              downloadInvoiceForOrder(item?.creditNoteMediaId);
                            }}
                            aria-label="download"
                            size={"xs"}
                            bg={"none"}
                          />
                        )}
                      </Box>

                      <UnorderedList
                        key={index}
                        listStyleType={"none"}
                        marginLeft={0}
                        marginBottom={"1em"}
                      >
                        {[
                          { key: "creditNoteNo", value: item?.creditNoteNo },
                          {
                            key: "creditNoteDate",
                            value: item?.creditNoteDate,
                          },
                          {
                            key: "creditNoteAmount",
                            value: item?.creditNoteAmount,
                          },
                        ].map(({ key, value }) =>
                          value != null && value !== "" ? (
                            <ListItem
                              display={"flex"}
                              key={key}
                              alignItems={"center"}
                            >
                              <Text fontSize={"sm"} minWidth={"90px"}>
                                {getTitleCaseFromCamelCase(key)}
                              </Text>
                              <Text fontSize={"sm"} marginRight={"5px"}>
                                :
                              </Text>
                              <Tooltip label={`${value}`}>
                                <Text
                                  fontSize={"sm"}
                                  noOfLines={1}
                                >{`${value}`}</Text>
                              </Tooltip>
                              {copierKeys.includes(key) && (
                                <GarrazCopyText
                                  text={String(value)}
                                  popOverMessage={`${key} Copied`}
                                />
                              )}
                            </ListItem>
                          ) : null,
                        )}
                      </UnorderedList>
                    </ListItem>
                  ) : (
                    <React.Fragment key={index} />
                  ),
                )}
              </UnorderedList>
            </Box>
          </>
        )}
      </PopoverContent>
      </Portal>
      {canVoidInvoice && orderNo && (
        <VoidOrderInvoiceConfirmationDialog
          isOpen={isVoidDialogOpen}
          onClose={onVoidDialogClose}
          orderNo={orderNo}
          invoiceNo={voidInvoiceNo}
          invoiceType={voidInvoiceType}
          onVoidSuccess={onVoidSuccess || (() => {})}
        />
      )}
      {(canRegenerateOrderInvoiceOption ||
        canRegenerateDeliveryInvoiceOption) && (
        <RegenerateInvoiceDialog
          isOpen={isRegenerateDialogOpen}
          onClose={onRegenerateDialogClose}
          canRegenerateOrderInvoice={canRegenerateOrderInvoiceOption}
          canRegenerateDeliveryInvoice={canRegenerateDeliveryInvoiceOption}
          hasOrderInvoice={hasOrderInvoice || false}
          hasDeliveryInvoice={hasDeliveryInvoice || false}
          onRegenerateOrderInvoice={
            canRegenerateOrderInvoiceOption
              ? onRegenerateOrderInvoice
              : undefined
          }
          onRegenerateDeliveryInvoice={
            canRegenerateDeliveryInvoiceOption
              ? onRegenerateDeliveryInvoice
              : undefined
          }
          currentDeliveryCost={currentDeliveryCost}
          minimumDeliveryCost={minimumDeliveryCost}
          isVirtualInvoiceRegenerationMode={isVirtualInvoiceRegenerationMode}
        />
      )}
    </Popover>
  );
};

export default InvoicePopver;
